<template>
  <div class="mobile-quick-create">
    <v-app-bar class="mobile-quick-create__app-bar" color="#102a43" dark height="64" elevation="0">
      <div class="mobile-quick-create__identity" data-testid="mobile-brand">
        <v-avatar tile size="34" class="mobile-quick-create__brand">
          <v-img src="/img/icons/android-chrome-192x192.png" alt="EGK"></v-img>
        </v-avatar>
        <div class="mobile-quick-create__identity-copy">
          <span class="mobile-quick-create__product">EGK CRM</span>
          <v-toolbar-title class="mobile-quick-create__title">
            {{ $t('mobile_quick_create_title') }}
          </v-toolbar-title>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        icon
        class="mobile-quick-create__logout"
        :disabled="submissionUnresolved"
        :aria-label="$t('app_bar_logout_btn')"
        @click="logout"
      >
        <v-icon>mdi-logout</v-icon>
      </v-btn>
    </v-app-bar>

    <v-container class="mobile-quick-create__content pa-0">
      <v-alert
        v-if="createdNumber"
        type="success"
        prominent
        class="mobile-quick-create__notice mx-3 mt-3 mb-3"
        aria-live="polite"
      >
        <div>{{ $t('mobile_quick_create_success_number', { number: createdNumber }) }}</div>
        <div v-if="createdRepairNumber">
          {{ $t('mobile_quick_create_success_repair_number', { number: createdRepairNumber }) }}
        </div>
        <v-btn color="white" class="black--text mt-3" @click="startAnother">
          {{ $t('mobile_quick_create_create_another') }}
        </v-btn>
      </v-alert>

      <v-alert
        v-if="submitError"
        type="error"
        dense
        class="mobile-quick-create__notice mx-3 mt-3 mb-3"
        aria-live="assertive"
      >
        {{ submitError }}
      </v-alert>

      <quick-create-form
        v-if="!permissionLoadError"
        v-show="!createdNumber"
        ref="form"
        :submitting="submitting"
        :submission-locked="pendingPayload !== null"
        :submission-blocked="terminalConflict"
        @submit="onSubmit"
      />
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { HttpMobileFix } from '@/js/http-Manager'
import { createRequestId, buildCreatePayload, mapCreateErrorKey } from '@/js/mobile-quick-create'
import QuickCreateForm from '@/components/MobileCustomWork/QuickCreateForm.vue'

function shouldUnlockSubmission (status) {
  return status >= 400 && status < 500 && status !== 409
}

export default {
  name: 'MobileQuickCreateView',
  components: {
    QuickCreateForm
  },
  data () {
    return {
      requestId: '',
      pendingPayload: null,
      submitting: false,
      createdNumber: '',
      createdRepairNumber: '',
      submitError: '',
      terminalConflict: false
    }
  },
  created () {
    this.requestId = createRequestId()
  },
  computed: {
    ...mapState('permission', {
      permissionLoadError: 'loadError'
    }),
    submissionUnresolved () {
      return this.submitting || this.pendingPayload !== null
    }
  },
  methods: {
    ...mapActions('login', {
      logoutAction: 'logout'
    }),
    async onSubmit (form) {
      if (this.submitting || this.terminalConflict) {
        return
      }

      this.submitError = ''
      this.submitting = true
      try {
        if (!this.pendingPayload) {
          this.pendingPayload = buildCreatePayload(this.requestId, form, form.fileIds)
        }
        const response = await HttpMobileFix.Create(this.pendingPayload)
        if (response && response.status === 200 && response.data && response.data.number) {
          this.pendingPayload = null
          this.createdNumber = response.data.number
          this.createdRepairNumber = response.data.repairNumber || ''
          return
        }

        this.applyCreateFailure(response)
      } catch (error) {
        const response = error && error.response
        this.applyCreateFailure(response)
      } finally {
        this.submitting = false
      }
    },
    applyCreateFailure (response) {
      const code = response && response.data && response.data.code
      this.submitError = this.$t(mapCreateErrorKey(code))
      if (
        code === 'temp_photo_expired' &&
        this.$refs.form &&
        typeof this.$refs.form.invalidateUploadedPhotos === 'function'
      ) {
        this.$refs.form.invalidateUploadedPhotos()
      }
      if (code === 'request_conflict') {
        this.terminalConflict = true
        return
      }
      if (response && shouldUnlockSubmission(response.status)) {
        this.pendingPayload = null
      }
    },
    startAnother () {
      if (!this.createdNumber || this.submissionUnresolved) {
        return
      }
      this.createdNumber = ''
      this.createdRepairNumber = ''
      this.submitError = ''
      this.requestId = createRequestId()
      this.pendingPayload = null
      if (this.$refs.form) {
        this.$refs.form.resetForm()
      }
    },
    logout () {
      if (this.submissionUnresolved) {
        return
      }
      this.logoutAction()
      this.$router.push(
        {
          path: '/login',
          query: { redirect: '/mobile_fix' }
        },
        () => {},
        () => {}
      )
    }
  }
}
</script>

<style scoped>
.mobile-quick-create {
  min-height: 100vh;
  min-height: 100dvh;
  background: #eef2f3;
  overscroll-behavior-y: contain;
}

.mobile-quick-create__app-bar {
  position: sticky !important;
  top: 0;
  z-index: 6;
  border-bottom: 3px solid #20a39e !important;
}

.mobile-quick-create__identity {
  display: flex;
  min-width: 0;
  align-items: center;
}

.mobile-quick-create__brand {
  flex: 0 0 auto;
  overflow: hidden;
  margin-right: 10px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  background: #ffffff;
}

.mobile-quick-create__identity-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
}

.mobile-quick-create__product {
  color: #a9d9d6;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
}

.mobile-quick-create__title {
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.25;
}

.mobile-quick-create__logout {
  min-width: 44px;
  min-height: 44px;
}

.mobile-quick-create__content {
  max-width: 680px;
  min-height: calc(100vh - 64px);
  min-height: calc(100dvh - 64px);
  background: #ffffff;
}

.mobile-quick-create__notice {
  border-radius: 6px;
  letter-spacing: 0;
}

@media (min-width: 681px) {
  .mobile-quick-create__content {
    border-right: 1px solid #d7e0e6;
    border-left: 1px solid #d7e0e6;
  }
}
</style>
